/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useCallback, useEffect, useMemo, useRef, useContext } from 'react'
import { Document } from 'react-pdf'
import { FixedSizeList as List, ListOnScrollProps } from 'react-window'
import { useMemoryManagement } from '../hooks/useMemoryManagement'
import { safariMemoryManager } from '../helpers/SafariMemoryManager'
import type { CSSProperties } from 'react'
import './Core.css'
import { VirtualizedPageItem } from './VirtualizedPageItem'
import { isPDFDocument, isPDFLoadingTask, isPDFRenderTask } from '../helpers/renderer'
import { StoreContext } from '@store/Root.store'
import { observer } from 'mobx-react'
import { LoadingScreen } from './LoadingScreen'
import { PdfErrorContainer } from './PdfErrorContainer'
import { getDocumentUrl } from '@/methods/getDocumentUrl'
import type { EditableFieldsConfig } from '@/components/EditableFields/EditableFieldsOverlay'

interface PDFViewerProps {
  file?: string
  documentUrl?: string
  onDocumentLoadSuccess?: (numPages: number) => void
  onError?: (error: Error) => void
  height?: number
  width?: number
  onDocumentLastPage?: () => void
  documentError: string | null
  setDocumentError: (error: string | null) => void
  setAllowSigning?: (allow: boolean) => void
  editableFields?: EditableFieldsConfig
}

export const Renderer: React.FC<PDFViewerProps> = observer(
  ({
    file,
    documentUrl,
    onDocumentLoadSuccess,
    onError,
    height = 800,
    width = 600,
    onDocumentLastPage,
    documentError,
    setDocumentError,
    setAllowSigning,
    editableFields,
  }) => {
    const store = useContext(StoreContext)
    const { totalPages, maxScrollOffset, pageScale } = store.PdfViewerState
    const { pdfViewer: trans } = store.TranslationsState.translationsNew

    // Memory leak prevention refs
    const mountedRef = useRef(true)
    const abortControllerRef = useRef<AbortController | null>(null)
    const loadingTaskRef = useRef<unknown>(null) // PDF.js LoadingTask
    const documentRef = useRef<unknown>(null) // PDF.js Document
    const renderTasksRef = useRef<Map<number, unknown>>(new Map()) // Page render tasks

    const [visiblePages, setVisiblePages] = useState<Set<number>>(new Set([1]))
    const [isLoading, setIsLoading] = useState(true)
    const [pageAspectRatio, setPageAspectRatio] = useState(1.414)

    const memoryManager = useMemoryManagement()

    const listRef = useRef<List>(null)

    const pageWidth = width - 40 // Account for padding
    const itemHeight = pageWidth * pageAspectRatio * pageScale + 24 // aspect ratio * scale + padding (space between pages)
    const capabilities = safariMemoryManager.getBrowserCapabilities()

    // Calculate max scroll offset when dimensions change
    const calculateAndSetMaxScrollOffset = useCallback(() => {
      if (totalPages > 0) {
        const totalContentHeight = totalPages * itemHeight
        const calculatedMaxScrollOffset = Math.max(0, totalContentHeight - height)
        store.PdfViewerState.setMaxScrollOffset(calculatedMaxScrollOffset)
      }
    }, [totalPages, itemHeight, height, store.PdfViewerState])

    // Update max scroll offset when relevant values change
    useEffect(() => {
      calculateAndSetMaxScrollOffset()
    }, [calculateAndSetMaxScrollOffset])

    // Register render task for tracking
    const registerRenderTask = useCallback((pageNumber: number, renderTask: unknown) => {
      if (renderTask) {
        renderTasksRef.current.set(pageNumber, renderTask)
      }
    }, [])

    // Unregister render task
    const unregisterRenderTask = useCallback((pageNumber: number) => {
      const renderTask = renderTasksRef.current.get(pageNumber)
      if (isPDFRenderTask(renderTask)) {
        try {
          renderTask.cancel()
        } catch (e) {
          // Silent cleanup
        }
      }
      renderTasksRef.current.delete(pageNumber)
    }, [])

    useEffect(() => {
      mountedRef.current = true
      abortControllerRef.current = new AbortController()

      return () => {
        mountedRef.current = false

        // Inline cleanup to avoid dependency loop
        // Cancel all active render tasks
        renderTasksRef.current.forEach((renderTask) => {
          if (isPDFRenderTask(renderTask)) {
            try {
              renderTask.cancel()
            } catch (e) {
              // Silent cleanup
            }
          }
        })
        renderTasksRef.current.clear()

        // Destroy PDF.js document
        if (isPDFDocument(documentRef.current)) {
          try {
            // Clean up all pages
            for (let i = 1; i <= totalPages; i++) {
              if (documentRef.current._transport?.pageCache) {
                delete documentRef.current._transport.pageCache[i - 1]
              }
            }
          } catch (e) {
            // Silent cleanup
          }
        }

        // Destroy PDF.js loading task (CRITICAL FOR MEMORY LEAKS)
        if (isPDFLoadingTask(loadingTaskRef.current)) {
          try {
            loadingTaskRef.current.destroy()
          } catch (e) {
            // Silent cleanup
          }
        }

        // Abort pending requests
        if (abortControllerRef.current) {
          try {
            abortControllerRef.current.abort()
          } catch (e) {
            // Silent cleanup
          }
        }

        // Force memory cleanup
        memoryManager.forceCleanup()

        // Clear refs
        loadingTaskRef.current = null
        documentRef.current = null
      }
    }, []) // Empty dependency array to prevent loops

    // Reset on file change
    useEffect(() => {
      if (file) {
        // Cancel render tasks
        renderTasksRef.current.forEach((renderTask) => {
          if (isPDFRenderTask(renderTask)) {
            try {
              renderTask.cancel()
            } catch (e) {
              // Silent cleanup
            }
          }
        })
        renderTasksRef.current.clear()

        // Destroy previous loading task
        if (isPDFLoadingTask(loadingTaskRef.current)) {
          try {
            loadingTaskRef.current.destroy()
          } catch (e) {
            // Silent cleanup
          }
        }

        // Reset state including max scroll offset
        store.PdfViewerState.setTotalPages(0)
        store.PdfViewerState.setMaxScrollOffset(0)
        setVisiblePages(new Set([1]))
        setIsLoading(true)
        setDocumentError(null)
        setPageAspectRatio(1.414)

        // Create new abort controller
        abortControllerRef.current = new AbortController()

        // Clear refs
        loadingTaskRef.current = null
        documentRef.current = null
      }
    }, [file]) // Only depend on file change

    const handleDocumentLoadSuccess = useCallback(
      async (pdfDocument: any) => {
        if (!mountedRef.current) return

        const { numPages } = pdfDocument

        // Store references for cleanup (CRITICAL FOR MEMORY LEAK PREVENTION)
        documentRef.current = pdfDocument
        if (pdfDocument.loadingTask) {
          loadingTaskRef.current = pdfDocument.loadingTask
        }

        try {
          const firstPage = await pdfDocument.getPage(1)
          const viewport = firstPage.getViewport({ scale: 1 })
          setPageAspectRatio(viewport.height / viewport.width)
        } catch {
          // Silent — keep default portrait A4 ratio
        }

        store.PdfViewerState.setTotalPages(numPages)
        setIsLoading(false)
        onDocumentLoadSuccess?.(numPages)

        // Calculate max scroll offset after setting total pages
        // This will be handled by the useEffect that watches totalPages

        // Safari-specific page loading strategy
        if (capabilities.isSafari) {
          // Load fewer pages initially on Safari
          setVisiblePages(new Set([1, 2].slice(0, Math.min(2, numPages))))
        } else {
          // Standard loading for other browsers
          setVisiblePages(new Set([1, 2, 3].slice(0, Math.min(3, numPages))))
        }
      },
      [onDocumentLoadSuccess, capabilities, store.PdfViewerState]
    )

    const handleDocumentLoadError = useCallback(
      (error: Error) => {
        setDocumentError(error.message)
        setIsLoading(false)
        onError?.(error)
      },
      [onError, setDocumentError]
    )

    const handleItemsRendered = useCallback(
      ({
        visibleStartIndex,
        visibleStopIndex,
      }: {
        visibleStartIndex: number
        visibleStopIndex: number
      }) => {
        const newVisiblePages = new Set<number>()

        // Safari-specific buffer strategy
        const buffer = capabilities.isSafari ? 1 : 2 // Smaller buffer on Safari

        for (
          let i = Math.max(0, visibleStartIndex - buffer);
          i <= Math.min(totalPages - 1, visibleStopIndex + buffer);
          i++
        ) {
          newVisiblePages.add(i + 1)
        }

        // Remove pages that are no longer visible
        const currentVisiblePages = visiblePages
        const pagesToRemove: number[] = []
        currentVisiblePages.forEach((pageNumber) => {
          if (!newVisiblePages.has(pageNumber)) {
            pagesToRemove.push(pageNumber)
          }
        })

        // Clean up removed pages
        pagesToRemove.forEach((pageNumber) => {
          memoryManager.removeRenderedPage(pageNumber)
        })

        setVisiblePages(newVisiblePages)
      },
      [totalPages, capabilities.isSafari, visiblePages]
    )

    const handleMemoryChange = useCallback(
      (
        pageNumber: number,
        width: number,
        height: number,
        pixelRatio: number,
        action: 'add' | 'remove'
      ) => {
        if (action === 'add') {
          memoryManager.addRenderedPage(pageNumber, width, height, pixelRatio)
        } else {
          memoryManager.removeRenderedPage(pageNumber)
        }
      },
      []
    )

    // Memory pressure monitoring
    useEffect(() => {
      if (capabilities.isSafari && memoryManager.memoryStats.isMemoryPressureHigh) {
        // Force cleanup of non-visible pages
        const currentTime = Date.now()
        const cleanupThreshold = 30000 // 30 seconds

        const currentVisiblePages = visiblePages
        currentVisiblePages.forEach((pageNumber) => {
          if (!currentVisiblePages.has(pageNumber)) {
            const pageInfo = memoryManager.getPageMemoryInfo(pageNumber)
            if (pageInfo && currentTime - pageInfo.timestamp > cleanupThreshold) {
              memoryManager.removeRenderedPage(pageNumber)
            }
          }
        })

        // Trigger garbage collection if available
        if ((window as any).gc) {
          try {
            ;(window as any).gc()
          } catch (e) {
            // Ignore
          }
        }
      }
    }, [memoryManager.memoryStats.isMemoryPressureHigh, capabilities.isSafari, visiblePages])

    const pdfjsOptions = useMemo(() => {
      // Memory leak prevention options
      const baseOptions = {
        // Disable caching to prevent memory buildup
        disableAutoFetch: false,
        disableStream: false,
        enableXfa: false,
        // Important: Set a reasonable cache size limit
        maxImageSize: capabilities.isSafari ? 5 * 1024 * 1024 : 10 * 1024 * 1024, // 5MB Safari, 10MB others
        cMapPacked: true,
        // Enable proper worker cleanup
        useWorkerFetch: true,
      }

      // Safari-specific optimizations
      if (capabilities.isSafari) {
        return {
          ...baseOptions,
          // Reduce memory usage on Safari
          renderInteractiveForms: false,
          enableWebGL: false, // Disable WebGL on Safari
          disableRange: false, // Allow range requests for better memory management
          verbosity: 0, // Reduce logging overhead
        }
      }

      return {
        ...baseOptions,
        disableRange: false,
        verbosity: 1,
      }
    }, [capabilities.isSafari])

    const onScrollHandler = useCallback(
      (e: ListOnScrollProps) => {
        // Calculate current page index based on scroll position
        const currentPageIndex = Math.floor(e.scrollOffset / itemHeight) + 1
        const clampedPageIndex = Math.max(1, Math.min(currentPageIndex, totalPages))

        // Calculate last page visibility
        const lastPageStartOffset = (totalPages - 1) * itemHeight
        const lastPageHalfVisible = e.scrollOffset + height >= lastPageStartOffset + itemHeight / 2

        // Update current page index in store
        store.PdfViewerState.setCurrentPageIndex(clampedPageIndex)

        // Trigger when last page is at least half visible
        if (totalPages > 0 && lastPageHalfVisible) {
          onDocumentLastPage && onDocumentLastPage()
        }

        store.PdfViewerState.showPageIndicator()
      },
      [store.PdfViewerState, maxScrollOffset, totalPages, itemHeight, height, onDocumentLastPage]
    )

    if (documentError) {
      return (
        <PdfErrorContainer
          width={width}
          height={height}
          failedToLoadPdf={trans?.cantDisplayTheDocument || ''}
          setAllowSigning={setAllowSigning}
          documentUrl={documentUrl}
        />
      )
    }

    return (
      <div
        className="pdf-core__viewer-container"
        style={
          {
            '--vc-width': typeof width === 'number' ? `${width}px` : width,
            '--vc-height': typeof height === 'number' ? `${height}px` : height,
          } as CSSProperties
        }
        data-testid="pdf-viewer"
        data-loaded={!isLoading}
      >
        <Document
          file={file}
          onLoadSuccess={handleDocumentLoadSuccess}
          onLoadError={handleDocumentLoadError}
          options={pdfjsOptions}
          loading={<></>}
          error={
            <PdfErrorContainer
              width={width}
              height={height}
              failedToLoadPdf={trans?.cantDisplayTheDocument || ''}
              setAllowSigning={setAllowSigning}
              documentUrl={documentUrl}
            />
          }
        >
          {isLoading ? (
            <LoadingScreen width={width} height={height}>
              <div className="pdf-core__loading-text">{trans?.loading}</div>
            </LoadingScreen>
          ) : (
            <List
              ref={listRef}
              className="pdf-viewer-virtualized-list"
              height={height}
              width={width}
              itemCount={totalPages}
              itemSize={itemHeight}
              onItemsRendered={handleItemsRendered}
              itemData={{
                numPages: totalPages,
                visiblePages,
                pageWidth,
                pageScale,
                pageAspectRatio,
                onMemoryChange: handleMemoryChange,
                isSafari: capabilities.isSafari,
                registerRenderTask,
                unregisterRenderTask,
                editableFields,
              }}
              onScroll={onScrollHandler}
            >
              {VirtualizedPageItem}
            </List>
          )}
        </Document>
      </div>
    )
  }
)
